import { useCallback, useMemo } from 'react';
import { format, parse, parseISO } from 'date-fns';
import {
  DateTimeFormat,
  DateTimeFormatConfigModel,
  DateTimeFormats,
  DateTimeFormatterFn,
  type DateTypes,
  DEFAULT_DATE_TIME_FORMAT,
  SEPARATOR_DICTIONARY,
  ThreeLetterDateFormats
} from './dateTimeFormatConfigModel';
import { useDateTimeFormatContext } from './DateTimeFormatProvider';

/**
 * Internal function used to build date time format strings based on the
 * passed in DateTimeFormatConfigModel object.
 *
 * Examples:
 *
 * short: dd MM yyyy e.g. 01 06 2023
 * long: d MMM yyyy e.g. 1 Jun 2023
 * shortDayMonth: dd MM e.g. 01 06
 * longDayMonth: d MMM e.g. 1 Jun
 * weekdayLong: EEEE, d MMM yyyy e.g. Monday, 1 Jun 2023
 * weekdayLongDayMonth: EEEE, d MMM e.g. Monday, 1 Jun
 * shortMonthYear: MM yyyy e.g. 06 2023
 * longMonthYear: MMM yyyy e.g. Jun 2023
 * shortMonth: MM e.g. 06
 * longMonth: MMM e.g. Jun
 * time: HH:mm e.g. 15:10
 * timeWithSeconds: HH:mm:ss e.g. 15:10:49
 * quarterYear: qqq yyyy e.g. Q2 2023
 * year: yyyy e.g. 2023
 * format: dmy or mdy or ymd
 *
 */
export const buildFormatStrings: (
  dateTimeFormatConfig: DateTimeFormatConfigModel,
  userDateFormat?: ThreeLetterDateFormats
) => DateTimeFormat = (
  dateTimeFormatConfig: DateTimeFormatConfigModel,
  userDateFormatOverride?: ThreeLetterDateFormats
) => {
  const shortMonth = 'MM';
  const longMonth = 'MMM';
  const year = 'yyyy';
  const quarterYear = 'qqq yyyy';

  let { dateOrder: format } = dateTimeFormatConfig;

  // Allow user to override the dateTimeFormatConfig format
  // via params.dateFormat specified in a Smart Form.
  if (userDateFormatOverride === 'dmy') {
    format = 'DAY_MONTH_YEAR';
  } else if (userDateFormatOverride === 'mdy') {
    format = 'MONTH_DAY_YEAR';
  } else if (userDateFormatOverride === 'ymd') {
    format = 'YEAR_MONTH_DAY';
  }

  const order = format.split('_').map((value) => {
    if (value === 'DAY') return { long: 'd', short: 'dd' };
    if (value === 'MONTH') return { long: longMonth, short: shortMonth };
    return { long: year, short: year };
  });
  const short = order.map((value) => value.short).join(SEPARATOR_DICTIONARY[dateTimeFormatConfig.separator]);
  const long = order
    .map((value) => {
      // Add comma after day when format is MONTH DAY YEAR e.g. Oct 3, 2020
      if (value.long === 'd' && format === 'MONTH_DAY_YEAR') {
        return `${value.long},`;
      }

      return value.long;
    })
    .join(' ');

  const time = dateTimeFormatConfig.use24HrTime ? 'HH:mm' : 'hh:mm a';
  const timeWithSeconds = dateTimeFormatConfig.use24HrTime ? 'HH:mm:ss' : 'hh:mm:ss a';
  const shortDayMonth = order
    .filter((format) => !format.short.includes('y'))
    .map((value) => value.short)
    .join(SEPARATOR_DICTIONARY[dateTimeFormatConfig.separator]);
  const longDayMonth = order
    .filter((format) => !format.long.includes('y'))
    .map((value) => value.long)
    .join(' ');
  const shortMonthYear = order
    .filter((format) => !format.short.includes('d'))
    .map((value) => value.short)
    .join(SEPARATOR_DICTIONARY[dateTimeFormatConfig.separator]);
  const longMonthYear = order
    .filter((format) => !format.long.includes('d'))
    .map((value) => value.long)
    .join(' ');
  const weekdayLongDayMonth = `EEEE, ${longDayMonth}`;
  const weekdayLong = `EEEE, ${long}`;
  const longMonthNameDayCommaYear = `MMM d, yyyy`;
  const fullMonthNameDayCommaYear = `MMMM d, yyyy`;
  const fullMonthNameYear = `MMMM yyyy`;

  let threeLetterDateFormat: ThreeLetterDateFormats;
  if (format === 'DAY_MONTH_YEAR') {
    threeLetterDateFormat = 'dmy';
  } else if (format === 'MONTH_DAY_YEAR') {
    threeLetterDateFormat = 'mdy';
  } else {
    threeLetterDateFormat = 'ymd';
  }

  return {
    short,
    long,
    shortDayMonth,
    longDayMonth,
    weekdayLong,
    weekdayLongDayMonth,
    shortMonthYear,
    longMonthYear,
    shortMonth,
    longMonth,
    time,
    timeWithSeconds,
    quarterYear,
    year,
    threeLetterDateFormat,
    longMonthNameDayCommaYear,
    fullMonthNameDayCommaYear,
    fullMonthNameYear
  };
};

/**
 * Return a date time string formats object based on the DateTimeFormatConfig object provided by
 * the wrapping DateTimeFormatProvider or uses the DEFAULT_DATE_TIME_FORMAT if not available.
 *
 * For example:
 *
 * const dateTimeFormat = useDateTimeFormat();
 *
 * Assuming Provider with { MONTH_DAY_YEAR, SLASH, use24HrTime } will return a date string format object:
 *
 * {
 *   short: 'MM/dd/yyyy',
 *   long: 'MMM/d/yyyy',
 *   shortDayMonth: 'MM/dd',
 *   longDayMonth: 'MMM/d',
 *   weekdayLong: 'E, MMM/d/yyyy',
 *   weekdayLongDayMonth: 'EEEE, MMM/d',
 *   shortMonthYear: 'MM/yyyy',
 *   longMonthYear: 'MMM/yyyy',
 *   shortMonth: 'MM',
 *   longMonth: 'MMM',
 *   time: 'HH:mm',
 *   timeWithSeconds: 'HH:mm:ss'
 *   quarterYear: 'Q yyyy',
 *   year: 'yyyy',
 *   threeLetterDateFormat: 'mdy'
 *   monthNameDayCommaYear: 'MMM d, yyyy' Jan 7, 2025
 * }
 *
 */
export function useDateTimeFormat(userDateFormatOverride?: ThreeLetterDateFormats) {
  const { dateTimeFormatConfig } = useDateTimeFormatContext();

  // Return as dateTimeFormat prop to ensure consistent naming when used.
  return {
    dateTimeFormat: buildFormatStrings(dateTimeFormatConfig || DEFAULT_DATE_TIME_FORMAT, userDateFormatOverride)
  };
}

/**
 * Return a date formatter function based on the DateTimeFormatConfig object provided by
 * the wrapping DateTimeFormatProvider or uses the DEFAULT_DATE_TIME_FORMAT if not available.
 *
 * For example:
 *
 * const { dateTimeFormatter } = useDateTimeFormatter();
 *
 * dateTimeFormatter('now', {long: true, time: true})
 *
 * dateTimeFormatter() can take 'now' as a date which is the same as new Date().
 *
 * By default all dates are considered local and will be displayed without any local time adjustment.
 * To treat dates as UTC set the utc prop to true.
 *
 * Note if the utc prop is specified it will always display the time with the browser time zone.
 * e.g. 23/09/2022 01:56 PM (GMT+1)
 *
 * Assuming DateTimeFormatProvider with { MONTH_DAY_YEAR, SLASH, use24HrTime } will return the date formatted as:
 *
 * 08/30/2022 18:00
 *
 * Example with format property specified:
 *
 * const { dateTimeFormatter } = useDateTimeFormatter();
 *
 * dateTimeFormatter('now', { format: DateTimeFormats.SHORT_MONTH_YEAR })
 *
 * Assuming DateTimeFormatProvider with { MONTH_DAY_YEAR, SLASH, use24HrTime } will return the date formatted as:
 *
 * 08/2022
 *
 * Note: if date is undefined it will return '-'
 *
 */
export function useDateTimeFormatter(userDateFormatOverride?: ThreeLetterDateFormats) {
  const { dateTimeFormatConfig } = useDateTimeFormatContext();
  const { dateTimeFormatterWithFormat } = useDateTimeFormatterWithFormat(
    dateTimeFormatConfig || DEFAULT_DATE_TIME_FORMAT,
    userDateFormatOverride
  );

  // Return as dateTimeFormatter prop to ensure consistent naming when used.
  return { dateTimeFormatter: dateTimeFormatterWithFormat };
}

/**
 * Return a date formatter function based on the passed in DateTimeFormatConfigModel object.
 *
 * NB: This should only be used internally by the Date Time DateTimeFormatConfigEditor
 *     preferences page.
 */
export const useDateTimeFormatterWithFormat: (
  dateTimeFormatConfig: DateTimeFormatConfigModel,
  userDateFormatOverride?: ThreeLetterDateFormats
) => {
  dateTimeFormatterWithFormat: DateTimeFormatterFn;
} = (dateTimeFormatConfig: DateTimeFormatConfigModel, userDateFormatOverride?: ThreeLetterDateFormats) => {
  const {
    short,
    long,
    shortDayMonth,
    longDayMonth,
    weekdayLong,
    weekdayLongDayMonth,
    shortMonthYear,
    longMonthYear,
    shortMonth,
    longMonth,
    time,
    timeWithSeconds,
    quarterYear,
    year,
    longMonthNameDayCommaYear,
    fullMonthNameDayCommaYear,
    fullMonthNameYear
  } = useMemo(
    () => buildFormatStrings(dateTimeFormatConfig, userDateFormatOverride),
    [dateTimeFormatConfig, userDateFormatOverride]
  );
  const dateTimeFormatterWithFormat: DateTimeFormatterFn = useCallback(
    (date, { format: dateTimeFormat, long: useLong, time: useTime, utc, noTimeZone, seconds } = {}) => {
      if (!date) {
        return '-';
      }

      let formatDate = '';

      switch (dateTimeFormat) {
        case DateTimeFormats.LONG_MONTH_NAME_DAY_COMMA_YEAR: {
          formatDate = longMonthNameDayCommaYear;
          break;
        }
        case DateTimeFormats.FULL_MONTH_NAME_DAY_COMMA_YEAR: {
          formatDate = fullMonthNameDayCommaYear;
          break;
        }
        case DateTimeFormats.FULL_MONTH_NAME_YEAR: {
          formatDate = fullMonthNameYear;
          break;
        }
        case DateTimeFormats.SHORT_DAY_MONTH: {
          formatDate = shortDayMonth;
          break;
        }
        case DateTimeFormats.LONG_DAY_MONTH: {
          formatDate = longDayMonth;
          break;
        }
        case DateTimeFormats.WEEKDAY_LONG: {
          formatDate = weekdayLong;
          break;
        }
        case DateTimeFormats.WEEKDAY_LONG_DAY_MONTH: {
          formatDate = weekdayLongDayMonth;
          break;
        }
        case DateTimeFormats.SHORT_MONTH_YEAR: {
          formatDate = shortMonthYear;
          break;
        }
        case DateTimeFormats.LONG_MONTH_YEAR: {
          formatDate = longMonthYear;
          break;
        }
        case DateTimeFormats.SHORT_MONTH: {
          formatDate = shortMonth;
          break;
        }
        case DateTimeFormats.LONG_MONTH: {
          formatDate = longMonth;
          break;
        }
        case DateTimeFormats.QUARTER_YEAR: {
          formatDate = quarterYear;
          break;
        }
        case DateTimeFormats.YEAR: {
          formatDate = year;
          break;
        }
        case DateTimeFormats.TIME: {
          useTime = true;
          break;
        }
        default: {
          formatDate = useLong ? long : short;
        }
      }

      const formatTime = `${useTime || (utc && useTime !== false) ? `${seconds ? timeWithSeconds : time}` : ''}${
        utc && !noTimeZone ? ` (O)` : ''
      }`;
      const formatString = [formatDate, formatTime].filter(Boolean).join(' ');

      // For users of this hook it is easier to understand that they need to specify the utc prop
      // to deal with UTC dates. However, internally it is easier to understand the parseDate function
      // if it is called local.
      const isLocal = !utc;
      return format(parseDate(date === 'now' ? new Date() : date, isLocal, useTime), formatString);
    },
    [
      timeWithSeconds,
      time,
      shortDayMonth,
      longDayMonth,
      weekdayLong,
      weekdayLongDayMonth,
      shortMonthYear,
      longMonthYear,
      shortMonth,
      longMonth,
      quarterYear,
      year,
      long,
      short,
      longMonthNameDayCommaYear,
      fullMonthNameDayCommaYear,
      fullMonthNameYear
    ]
  );

  // Return as dateTimeFormatterWithFormat prop to ensure consistent naming when used.
  return { dateTimeFormatterWithFormat };
};

/**
 * Generate a Date object in local time if the local prop is set to true (the default) keeping the original
 * date and time, otherwise, treat as UTC and adjust the date according to the local time zone.
 *
 * @param date The date as a string or Date object to convert to a local Date object.
 * @returns Converted Date in local time.
 */
function parseDate(date?: DateTypes, local: boolean = true, keepTime: boolean = false): Date {
  const inputFormat = 'yyyy-MM-dd';

  if (typeof date === 'string') {
    const isISO = date.includes('T') && date.includes('Z');
    if (isISO) {
      return local ? removeTimeZone(parseISO(date), keepTime) : parseISO(date);
    }

    const fixedDate = deprecatedFixDate(date, local, keepTime, inputFormat);
    if (fixedDate) {
      return fixedDate;
    }

    return local ? parse(date, inputFormat, new Date()) : new Date(date);
  }
  // TODO: Potential Bug, please fix this
  // @ts-ignore: Date can be undefined, so potentially this part of code include a bug.
  return local ? removeTimeZone(typeof date === 'number' ? new Date(date) : date, keepTime) : new Date(date);
}

/**
 * None of this should be required. Bringing over from old code as a just in case. Will remove later.
 */
function deprecatedFixDate(date: string, local: boolean, keepTime: boolean, inputFormat: string): Date | false {
  // How can date include GMT or UTC??? Are we really passing in date formatted strings here?
  const isGMT = date.includes('GMT');
  const isUTC = date.includes('UTC');
  if (isGMT || isUTC) {
    return local ? removeTimeZone(new Date(date), keepTime) : new Date(date);
  }

  if (date.includes('T')) {
    // LocalDateTime does not include a time zone so add a 'Z' to make it ISO compliant.
    // LocalDateTime is to be removed hence it is placed in deprecatedFixDate.
    return local ? removeTimeZone(parseISO(`${date}Z`), keepTime) : parseISO(`${date}Z`);
  }

  // Date must be a simple date without any time information. e.g. 2022-08-23
  if (date.includes('/')) {
    // '/' probably due to bug in FE or BE.
    const dateWithHyphens = date.replace(/\//g, '-');
    return local ? parse(dateWithHyphens, inputFormat, new Date()) : new Date(dateWithHyphens); // MM/dd/yyyy
  }

  return false;
}

export function removeTimeZone(date: Date, keepTime?: boolean) {
  return keepTime
    ? new Date(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds()
      )
    : new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0);
}
